

import React, { ComponentProps, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { ModalCloseText, ModalHeaderContainer, ModalTitle } from './styledProductHeaderContent'
import { ModalBackground, ModalMainContent, ModalOverlayContainer, ModalOverlayWrapper } from './styledProductOverlayWrapper'

export type ModalProps = Omit<
ComponentProps<typeof ModalOverlayWrapper>,
'open' | 'animationTime' | '$fullWidth' | 'transition'
> & {
  open?: boolean
  transition?: 'opacity' | 'slide-right',
  animationDuration?: number
  portal?: string
  title?: React.ReactNode | Element
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClose?: any
  fullWidth?: boolean
  showBackground?: boolean
  lock?: boolean //prevent Modal from closing when open
  disabledBackdropClose?: boolean
  disableClose?: boolean
  slotProps?: {
    header?: Pick<ComponentProps<typeof ModalHeaderContainer>, 'sx'>
    modalMain?: Pick<ComponentProps<typeof ModalMainContent>, 'sx'>
  }
}

export const Modal: React.FC<ModalProps> = ({
  open,
  showBackground = true,
  portal = '#modal',
  title,
  onClose,
  transition = 'opacity',
  animationDuration = 250,
  children,
  fullWidth,
  disabledBackdropClose,
  disableClose,
  slotProps,
  ...props
}) => {
  const [shouldRender, setShouldRender] = useState(open)
  const [activeElement, setActiveElement] = useState<HTMLElement>()
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    if (open) {
      setActiveElement(document.activeElement as HTMLElement)
      setShouldRender(open)
    } else {
      const animateTimeout = setTimeout(() => {
        setShouldRender(open)
        activeElement?.focus()
      }, animationDuration - 20)

      // Fix an issue in Safari where the modal would not animate out
      if (ref && ref.current && transition === 'opacity') {
        ref.current.style.opacity = '0'
      }

      return () => {
        clearTimeout(animateTimeout)
      }
    }
  }, [open, transition, animationDuration, activeElement, ref])

  if (!shouldRender) {
    return null
  }

  const portalEl = document.querySelector(portal)

  const inner = (
    <ModalOverlayWrapper
      ref={ref}
      $fullWidth={fullWidth}
      open={open}
      animationTime={animationDuration}
      transition={transition}
      {...props}
    >
      {showBackground && <ModalBackground onClick={disableClose || disabledBackdropClose ? () => { } : onClose} />}
      <ModalOverlayContainer $fullWidth={fullWidth}>
        {title && (
          <ModalHeaderContainer {...slotProps?.header}>
            <ModalTitle variant="body2">{title}</ModalTitle>
            {onClose &&
              <ModalCloseText text round disabled={disableClose} onClick={onClose}>
                Close
              </ModalCloseText>
            }
          </ModalHeaderContainer>
        )}
        <ModalMainContent id="__modal_main" $fullWidth={fullWidth} {...slotProps?.modalMain}>
          {children}
        </ModalMainContent>
      </ModalOverlayContainer>
    </ModalOverlayWrapper >
  )

  return portalEl ? createPortal(inner, portalEl) : inner
}
